import { useEffect, useMemo, useState } from "react";

import { UsersIcon } from "@/assets/icons";
import type { StatusFilterType } from "@/components/admin/companies";
import { CompaniesList, CompaniesToolbar } from "@/components/admin/companies";
import { strings } from "@/constants";
import { useListCompanies } from "@/hooks";
import type { CompanyType } from "@/types";

const matchesTermHandler = (company: CompanyType, term: string): boolean => {
    const {
        email,
        name,
    } = company;

    if (!term) return true;

    const haystacks = [name, email];

    return haystacks.some((value) => value.toLowerCase().includes(term));
};

const matchesStatusHandler = (company: CompanyType, status: StatusFilterType): boolean => {
    const { isEnabled } = company;

    if (status === "all") return true;

    if (status === "enabled") return isEnabled;

    return !isEnabled;
};

export const AdminCompaniesPage = () => {
    const [search, setSearch] = useState("");

    const [statusFilter, setStatusFilter] = useState<StatusFilterType>("all");

    const {
        data,
        isLoading,
        onRun: fetchCompaniesHandler,
    } = useListCompanies();

    const filteredCompanies = useMemo(
        () => {
            if (!data) return [];

            const term = search.trim().toLowerCase();

            return data.filter((company) => matchesTermHandler(
                company,
                term,
            ) && matchesStatusHandler(
                company,
                statusFilter,
            ));
        },
        [
            data,
            search,
            statusFilter,
        ],
    );

    useEffect(
        () => void fetchCompaniesHandler({}),
        [fetchCompaniesHandler],
    );

    return (
        <div
            className="
                mx-auto
                w-full
                max-w-5xl
                space-y-6
            "
        >
            <div className="flex items-center gap-2 sm:gap-3">
                <UsersIcon className="h-6 w-6 shrink-0 text-primary sm:h-7 sm:w-7" />
                <h1 className="text-xl font-bold text-text-primary sm:text-2xl">{strings.admin.companiesTitle}</h1>
            </div>
            <CompaniesToolbar
                search={search}
                statusFilter={statusFilter}
                onSearchChange={setSearch}
                onStatusChange={setStatusFilter}
            />
            <CompaniesList
                companies={filteredCompanies}
                isLoading={isLoading}
            />
        </div>
    );
};
